import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

export type CustomConditionType = 'noEventId' | string;

export interface ResearchAssistantMatcher {
  path: string;
  isRegexPath?: boolean;
  conditions?: {
    queryParams?: Record<string, string | boolean | undefined>;
    customCondition?: CustomConditionType;
  };
}

export interface ResearchAssistantStoreState {
  isOpen: boolean;
  isEnabled: boolean;
  allowMessageSubmit: boolean;
  availabilityMatchers: ResearchAssistantMatcher[];
  // Mirrors researchAssistantSlice.isProcessing — kept in sync by
  // useSyncIsWaitingForResponse while the chat-history slice still lives in
  // Redux (migration out of scope for this ticket).
  isWaitingForResponse: boolean;
}

export interface ResearchAssistantStoreActions {
  openResearchAssistant: () => void;
  closeResearchAssistant: () => void;
  toggleResearchAssistant: () => void;
  setResearchAssistantEnabled: (enabled: boolean) => void;
  addAvailabilityMatcher: (matcher: ResearchAssistantMatcher) => void;
  updateAvailabilityMatchers: (matchers: ResearchAssistantMatcher[]) => void;
  setAllowMessageSubmit: (allow: boolean) => void;
  setIsWaitingForResponse: (waiting: boolean) => void;
  resetGlobalResearchAssistantState: () => void;
}

export const INITIAL_AVAILABILITY_MATCHERS: ResearchAssistantMatcher[] = [
  // RA on cohort builder events tab (legacy path)
  {
    path: '/projects/.*/cohort-builder',
    isRegexPath: true,
    conditions: {
      queryParams: { pageTab: 'events' },
      customCondition: 'noEventId',
    },
  },
  // RA on cohort builder definition tab (legacy path)
  {
    path: '/projects/.*/cohort-builder',
    isRegexPath: true,
    conditions: {
      queryParams: { pageTab: 'definition' },
      customCondition: 'noEventId',
    },
  },
  // RA on standalone analytical variable edit page
  {
    path: '/projects/.*/analytical-variable/.*/edit',
    isRegexPath: true,
  },
  // RA on agent-first project landing page. Phase 4 widens this to cover
  // nested artifact routes via a runtime-added matcher from `ProjectPage`
  // (only mounts when gen_3 is ON). The landing path also backs the
  // analytical-dataset list filter (`?kind=analytical_dataset`), which has no
  // chat surface — exclude it here too, since this matcher is OR'd with the
  // runtime one and would otherwise re-enable RA there.
  // TODO: cleanup with research_assistant_gen_3 ff
  {
    path: '^/projects/[^/]+/?$',
    isRegexPath: true,
    conditions: {
      customCondition: 'notAnalyticalDatasetPage',
    },
  },
];

const initialState: ResearchAssistantStoreState = {
  isOpen: false,
  isEnabled: false,
  allowMessageSubmit: true,
  availabilityMatchers: INITIAL_AVAILABILITY_MATCHERS,
  isWaitingForResponse: false,
};

export const useResearchAssistantStore = create<
  ResearchAssistantStoreState & ResearchAssistantStoreActions
>()(
  devtools(
    (set) => ({
      ...initialState,
      openResearchAssistant: () => set({ isOpen: true }, false, 'openResearchAssistant'),
      closeResearchAssistant: () => set({ isOpen: false }, false, 'closeResearchAssistant'),
      toggleResearchAssistant: () =>
        set((state) => ({ isOpen: !state.isOpen }), false, 'toggleResearchAssistant'),
      setResearchAssistantEnabled: (enabled) =>
        set({ isEnabled: enabled }, false, 'setResearchAssistantEnabled'),
      addAvailabilityMatcher: (matcher) =>
        set(
          (state) => ({ availabilityMatchers: [...state.availabilityMatchers, matcher] }),
          false,
          'addAvailabilityMatcher'
        ),
      updateAvailabilityMatchers: (matchers) =>
        set({ availabilityMatchers: matchers }, false, 'updateAvailabilityMatchers'),
      setAllowMessageSubmit: (allow) =>
        set({ allowMessageSubmit: allow }, false, 'setAllowMessageSubmit'),
      setIsWaitingForResponse: (waiting) =>
        set({ isWaitingForResponse: waiting }, false, 'setIsWaitingForResponse'),
      resetGlobalResearchAssistantState: () =>
        set(initialState, false, 'resetGlobalResearchAssistantState'),
    }),
    { name: 'research-assistant-store' }
  )
);

// ---------------------------------------------------------------------------
// Selector hooks
// ---------------------------------------------------------------------------

export const useIsResearchAssistantOpen = (): boolean => useResearchAssistantStore((s) => s.isOpen);

export const useIsResearchAssistantEnabled = (): boolean =>
  useResearchAssistantStore((s) => s.isEnabled);

export const useAllowMessageSubmit = (): boolean =>
  useResearchAssistantStore((s) => s.allowMessageSubmit);

export const useAvailabilityMatchers = (): ResearchAssistantMatcher[] =>
  useResearchAssistantStore(useShallow((s) => s.availabilityMatchers));

export const useIsWaitingForResponse = (): boolean =>
  useResearchAssistantStore((s) => s.isWaitingForResponse);

// ---------------------------------------------------------------------------
// Action helpers — call from non-React code or to avoid hook ergonomics
// ---------------------------------------------------------------------------

export const openResearchAssistant = (): void =>
  useResearchAssistantStore.getState().openResearchAssistant();

export const closeResearchAssistant = (): void =>
  useResearchAssistantStore.getState().closeResearchAssistant();

export const toggleResearchAssistant = (): void =>
  useResearchAssistantStore.getState().toggleResearchAssistant();

export const setResearchAssistantEnabled = (enabled: boolean): void =>
  useResearchAssistantStore.getState().setResearchAssistantEnabled(enabled);

export const addAvailabilityMatcher = (matcher: ResearchAssistantMatcher): void =>
  useResearchAssistantStore.getState().addAvailabilityMatcher(matcher);

export const updateAvailabilityMatchers = (matchers: ResearchAssistantMatcher[]): void =>
  useResearchAssistantStore.getState().updateAvailabilityMatchers(matchers);

export const setAllowMessageSubmit = (allow: boolean): void =>
  useResearchAssistantStore.getState().setAllowMessageSubmit(allow);

export const setIsWaitingForResponse = (waiting: boolean): void =>
  useResearchAssistantStore.getState().setIsWaitingForResponse(waiting);

export const resetGlobalResearchAssistantState = (): void =>
  useResearchAssistantStore.getState().resetGlobalResearchAssistantState();
